import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AuthShell } from "../auth/AuthShell";
import { getIdToken } from "@/lib/firebase";
import { config } from "@/config";

/** Per-seat alert delivery settings — wired to GET/PUT /api/delivery + POST /api/delivery/test.
 *  Each operator routes alerts to their own Telegram / Slack / webhook, filtered by severity,
 *  trading areas and a ticker watchlist. */

interface DeliveryProfile {
  email: string;
  enabled: boolean;
  telegramChatId: string | null;
  slackWebhookUrl: string | null;
  webhookUrl: string | null;
  webhookSecret: string | null;
  minSeverity: "low" | "medium" | "high" | "critical";
  areas: string[];
  tickers: string[];
}

interface TestResult { channel: string; ok: boolean; error?: string }

const AREAS = [
  { key: "energy", label: "Energy & power" },
  { key: "natural", label: "Natural events" },
  { key: "geopolitical", label: "Geopolitical" },
  { key: "maritime", label: "Maritime" },
] as const;

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getIdToken();
  const r = await fetch(`${config.restUrl}${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${token}`, "content-type": "application/json", ...(init?.headers ?? {}) },
  });
  const body = (await r.json().catch(() => ({}))) as T & { error?: string };
  if (!r.ok) throw new Error(body.error || `HTTP ${r.status}`);
  return body;
}

export function DeliveryScreen() {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [testResults, setTestResults] = useState<TestResult[] | null>(null);

  const [enabled, setEnabled] = useState(true);
  const [telegramChatId, setTelegramChatId] = useState("");
  const [slackWebhookUrl, setSlackWebhookUrl] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [minSeverity, setMinSeverity] = useState<DeliveryProfile["minSeverity"]>("high");
  const [areas, setAreas] = useState<string[]>([]);
  const [tickers, setTickers] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { profile } = await api<{ profile: DeliveryProfile | null }>("/api/delivery");
        if (!alive) return;
        if (profile) {
          setEnabled(profile.enabled);
          setTelegramChatId(profile.telegramChatId ?? "");
          setSlackWebhookUrl(profile.slackWebhookUrl ?? "");
          setWebhookUrl(profile.webhookUrl ?? "");
          setWebhookSecret(profile.webhookSecret ?? "");
          setMinSeverity(profile.minSeverity);
          setAreas(profile.areas);
          setTickers(profile.tickers.join(", "));
        }
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : "failed to load profile");
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  const hasChannel = Boolean(telegramChatId.trim() || slackWebhookUrl.trim() || webhookUrl.trim());

  async function save(): Promise<boolean> {
    setBusy(true);
    setError(null);
    setNotice(null);
    setTestResults(null);
    try {
      await api<{ profile: DeliveryProfile | null }>("/api/delivery", {
        method: "PUT",
        body: JSON.stringify({
          enabled,
          telegramChatId: telegramChatId.trim() || null,
          slackWebhookUrl: slackWebhookUrl.trim() || null,
          webhookUrl: webhookUrl.trim() || null,
          webhookSecret: webhookSecret.trim() || null,
          minSeverity,
          areas,
          tickers: tickers.split(",").map((t) => t.trim()).filter(Boolean),
        }),
      });
      setNotice("Saved. New alerts route to these channels within a minute.");
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : "save failed");
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function testFire() {
    // Save first so the test always fires against what's on screen.
    const ok = await save();
    if (!ok) return;
    setBusy(true);
    setNotice(null);
    try {
      const { results } = await api<{ results: TestResult[] }>("/api/delivery/test", { method: "POST" });
      setTestResults(results);
    } catch (e) {
      setError(e instanceof Error ? e.message : "test fire failed");
    } finally {
      setBusy(false);
    }
  }

  function toggleArea(key: string) {
    setAreas((prev) => (prev.includes(key) ? prev.filter((a) => a !== key) : [...prev, key]));
  }

  return (
    <AuthShell title="ALERT DELIVERY" subtitle="Route signals to your desk">
      {!loaded ? (
        <p className="auth-copy">Loading delivery profile…</p>
      ) : (
        <form className="auth-form" onSubmit={(e) => { e.preventDefault(); void save(); }}>
          <label className="dl-toggle">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <span className="auth-label" style={{ letterSpacing: "0.14em" }}>Delivery enabled</span>
          </label>

          <label className="auth-field">
            <span className="auth-label">Telegram chat ID</span>
            <input value={telegramChatId} onChange={(e) => setTelegramChatId(e.target.value)} placeholder="e.g. 123456789" autoComplete="off" />
            <span className="dl-hint">Message the EARLYWIRE bot, then paste the chat ID it replies with.</span>
          </label>

          <label className="auth-field">
            <span className="auth-label">Slack incoming-webhook URL</span>
            <input value={slackWebhookUrl} onChange={(e) => setSlackWebhookUrl(e.target.value)} placeholder="https://hooks.slack.com/services/…" autoComplete="off" />
          </label>

          <label className="auth-field">
            <span className="auth-label">Webhook URL</span>
            <input value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} placeholder="https://your-desk.example.com/earlywire" autoComplete="off" />
          </label>

          <label className="auth-field">
            <span className="auth-label">Webhook signing secret</span>
            <input value={webhookSecret} onChange={(e) => setWebhookSecret(e.target.value)} placeholder="optional — HMAC-signs each POST" autoComplete="off" />
          </label>

          <label className="auth-field">
            <span className="auth-label">Minimum severity to push</span>
            <select value={minSeverity} onChange={(e) => setMinSeverity(e.target.value as DeliveryProfile["minSeverity"]) }>
              <option value="low">LOW — everything</option>
              <option value="medium">MEDIUM</option>
              <option value="high">HIGH (recommended)</option>
              <option value="critical">CRITICAL only</option>
            </select>
          </label>

          <div className="auth-field">
            <span className="auth-label">Trading areas · empty = all</span>
            <div className="dl-areas">
              {AREAS.map((a) => (
                <label key={a.key} className={`dl-chip${areas.includes(a.key) ? " on" : ""}`}>
                  <input type="checkbox" checked={areas.includes(a.key)} onChange={() => toggleArea(a.key)} />
                  {a.label}
                </label>
              ))}
            </div>
            <span className="dl-hint">High & critical signals are always pushed regardless of area.</span>
          </div>

          <label className="auth-field">
            <span className="auth-label">Ticker watchlist · empty = all</span>
            <input value={tickers} onChange={(e) => setTickers(e.target.value)} placeholder="e.g. ENGI.PA, RWE.DE, TTF" autoComplete="off" />
          </label>

          {error && <div className="auth-error">{error}</div>}
          {notice && <div className="dl-notice">{notice}</div>}
          {testResults && (
            <div className="dl-results">
              {testResults.map((r) => (
                <div key={r.channel} className={r.ok ? "ok" : "fail"}>
                  {r.ok ? "✓" : "✗"} {r.channel}{r.error ? ` — ${r.error}` : " — delivered"}
                </div>
              ))}
            </div>
          )}

          <div className="acct-actions">
            <button className="auth-btn primary" type="submit" disabled={busy}>
              {busy ? "WORKING…" : "SAVE"}
            </button>
            <button className="auth-btn" type="button" disabled={busy || !hasChannel} title={hasChannel ? "Save + send a sample alert to your channels" : "Add at least one channel first"} onClick={() => void testFire()}>
              TEST FIRE
            </button>
            <Link className="auth-btn" to="/account">← ACCOUNT</Link>
          </div>
        </form>
      )}
    </AuthShell>
  );
}
